// src/pages/PrivacyPolicy.tsx
import React from 'react';
import styles from './StaticPage.module.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className={styles.pageContainer}>
      {/* ✅ Static compliance block for Google crawlers (invisible to users) */}
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        Privacy Policy for TFIC Event Companion (package name: com.tfic.tficmobileapp),
        published on Google Play by DigitalMaster Entertainment.
      </div>

      <h1 className={styles.pageTitle}>Privacy Policy</h1>

      <h2 className={styles.sectionTitle}>1. Introduction</h2>
      <p>
        This Privacy Policy applies to the <strong>TFIC Event Companion</strong> mobile application 
        (package name: <code>com.tfic.tficmobileapp</code>), which is published on Google Play under the official developer account, 
        <strong> DigitalMaster Entertainment</strong>. 
      </p>
      <p>
        DigitalMaster Entertainment, in partnership with <strong>The Frontier Initiative Corporation (TFIC)</strong>, 
        is committed to protecting the privacy of our users. This policy explains how we collect, use, and safeguard your information 
        when you visit our website, register as a member, or use the TFIC Event Companion app. 
        By using our website or mobile app, you consent to the data practices described in this policy.
      </p>      

      <h2 className={styles.sectionTitle}>2. Information We Collect</h2>
      <p>We collect the following types of information to provide and improve our services:</p>
      <ul className={styles.list}>
        <li><strong>Personal Information:</strong> When you register for membership or contact us, we may collect personal information such as your name, email address, and other details necessary for account setup.</li>
        <li><strong>Usage Data:</strong> We automatically collect information about your interactions with our website, including IP address, browser type, and pages visited. This helps us improve our services and analyze site usage.</li>
        <li><strong>Cookies:</strong> Our website may use cookies to enhance user experience. Cookies are small files stored on your device to remember your preferences. You can control cookie settings through your browser.</li>
      </ul>
      <p>
        When you use the <strong>TFIC Event Companion</strong> app, we may also collect limited information related to app functionality, 
        such as event RSVPs, device information, and notification preferences. This is used solely to provide event calendar features and improve your app experience.
      </p>
    
      <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
      <p>We may use the information we collect for various purposes, including:</p>
      <ul className={styles.list}>
        <li>To create and manage your membership account.</li>
        <li>To provide event calendar features, including RSVPs and notifications within the TFIC Event Companion app.</li>
        <li>To send you updates about your account, including login information and changes to our policies.</li>
        <li>To improve our website functionality and content based on user feedback.</li>
        <li>To comply with legal obligations.</li>
      </ul>

      <h2 className={styles.sectionTitle}>4. Medical SOS Emergency System</h2>
      <p>
        Our Medical SOS system allows members to request in-game medical assistance. When you create a medical ticket, we collect:
      </p>
      <ul className={styles.list}>
        <li><strong>Location Information:</strong> Your current in-game star system and location.</li>
        <li><strong>Emergency Details:</strong> Death timer remaining, notes about your situation.</li>
        <li><strong>Communication Logs:</strong> Chat messages between you and assigned medics.</li>
        <li><strong>Operational Data:</strong> Ticket status, response times, medic assignments.</li>
      </ul>
      <p>
        <strong>Data Retention:</strong> Medical tickets and associated chat logs are automatically deleted after 90 days. 
        Aggregated statistical reports (without personally identifiable information) may be retained for up to 1 year for operational analysis.
      </p>
      <p>
        <strong>Access:</strong> Your medical tickets and chat logs are visible to assigned medics and administrators for coordination and moderation purposes. 
        There is no expectation of privacy in medical communications, as they are operational in nature.
      </p>
      <p>
        <strong>Purpose:</strong> This data is collected solely to facilitate in-game emergency medical response and improve service quality. 
        It is not used for marketing or sold to third parties.
      </p>

      <h2 className={styles.sectionTitle}>5. Data Retention</h2>
      <p>
        We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy. 
        When your information is no longer needed, we will securely delete or anonymize it.
      </p>

      <h2 className={styles.sectionTitle}>6. Data Sharing and Security</h2>
      <p>
        We do not sell or rent your information to third parties. We may share your data with trusted service providers 
        who assist us in operating our website and mobile app. We have implemented security measures to protect your information from 
        unauthorized access, use, or disclosure.
      </p>

      <h2 className={styles.sectionTitle}>7. Your Rights</h2>
      <p>You have the right to:</p>
      <ul className={styles.list}>
        <li>Access the personal information we hold about you.</li>
        <li>Request corrections to your information if it is inaccurate.</li>
        <li>Request the deletion of your information under certain circumstances.</li>
        <li>Withdraw your consent for specific data processing activities.</li>
      </ul>
      <p>
        If you would like to exercise any of these rights, please contact us via our Data Request Form or email us at 
        <a href="mailto:administration@tficorg.org"> administration@tficorg.org</a>.
      </p>

      <h2 className={styles.sectionTitle}>8. Third-Party Links</h2>
      <p>
        Our website may contain links to third-party websites. We are not responsible for the privacy practices or content 
        of these sites. We encourage users to read the privacy policies of each site they visit.
      </p>

      <h2 className={styles.sectionTitle}>9. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy periodically. Changes will be posted on this page, and we encourage you to review 
        the policy regularly to stay informed about how we protect your information.
      </p>

      <h2 className={styles.sectionTitle}>10. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or your personal information, please contact us at 
        <a href="mailto:administration@tficorg.org"> administration@tficorg.org</a>. 
      </p>
      <p>
        For inquiries related specifically to the <strong>TFIC Event Companion</strong> mobile application, you may also reach out to the developer, 
        <strong>DigitalMaster Entertainment</strong>, via the same email address.
      </p>
      
      <p className={styles.footer}>
        Last updated: December 5, 2025
      </p>
      <p className={styles.helpLink}>
        Need help? <a href="/data-request">Submit a Data Request</a>
      </p>
    </div>
  );
};

export default PrivacyPolicy;
